package com.eladfinish.bible.contest.screens.pairMatching.ui

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.copied_to_clipboard
import com.eladfinish.bible.contest.data.common.ClipboardCopier
import com.eladfinish.bible.contest.di.getKoinInstance
import com.eladfinish.bible.contest.navigation.PairMatchingScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.screenKey
import com.eladfinish.bible.contest.platform.audio.SoundEffect
import com.eladfinish.bible.contest.platform.audio.SoundService
import com.eladfinish.bible.contest.platform.haptics.HapticService
import com.eladfinish.bible.contest.screens.pairMatching.PairMatchingAction
import com.eladfinish.bible.contest.screens.pairMatching.PairMatchingEffect
import com.eladfinish.bible.contest.screens.pairMatching.PairMatchingState
import com.eladfinish.bible.contest.screens.pairMatching.PairMatchingStore
import com.eladfinish.bible.contest.screens.pairMatching.model.GamePhase
import com.eladfinish.bible.contest.screens.pairMatching.model.KeyItem
import com.eladfinish.bible.contest.screens.pairMatching.model.PairItem
import com.eladfinish.bible.contest.screens.pairMatching.model.PairsTopicItem
import com.eladfinish.bible.contest.screens.pairMatching.model.SpecialTopicItem
import com.eladfinish.bible.contest.screens.pairMatching.model.ValueItem
import com.eladfinish.bible.contest.screens.pairMatching.ui.dialogs.PairMatchingDialogs
import com.eladfinish.bible.contest.ui.common.PreviewTheme
import com.eladfinish.bible.contest.ui.common.SetScreenIsLoading
import com.eladfinish.bible.contest.ui.menus.actions.PairMatchingToolbarAction
import com.eladfinish.bible.contest.ui.menus.viewModels.ToolbarActionBusViewModel
import dev.icerock.moko.resources.StringResource
import dev.icerock.moko.resources.desc.StringDesc
import kotlinx.collections.immutable.toImmutableList
import org.koin.compose.viewmodel.koinViewModel

private val screenKey = PairMatchingScreenNavigationDestination::class.screenKey

/**
 * Full CMP PairMatching screen (V2).
 *
 * Wires [PairMatchingStore] → dialogs and either [PairMatchingTwoColumnGame]
 * or [SortableList] depending on the active [GamePhase].
 */
@Composable
fun PairMatchingScreen(
    toolbarActionBus: ToolbarActionBusViewModel,
    showToast: (String) -> Unit,
    showToastDesc: (StringDesc) -> Unit,
    showToastRes: (StringResource) -> Unit,
    onNavigateToMultipleChoice: () -> Unit,
    onOpenSettings: () -> Unit,
    store: PairMatchingStore = koinViewModel(),
    soundService: SoundService = getKoinInstance(),
    hapticService: HapticService = getKoinInstance(),
    clipboardCopier: ClipboardCopier = getKoinInstance(),
) {
    val state by store.observeState().collectAsState()

    // ── Trigger initial data load ──────────────────────────────────────────────
    LaunchedEffect(Unit) {
        store.dispatch(PairMatchingAction.LoadTopics)
    }

    // ── Side-effect handler ────────────────────────────────────────────────────
    LaunchedEffect(Unit) {
        store.observeSideEffect().collect { effect ->
            when (effect) {
                is PairMatchingEffect.ShowToast ->
                    showToast(effect.message)

                is PairMatchingEffect.ShowToastDesc ->
                    showToastDesc(effect.message)

                PairMatchingEffect.PlaySuccessSound ->
                    soundService.playIfAllowed(SoundEffect.SUCCESS)

                is PairMatchingEffect.VibrateCorrect ->
                    hapticService.vibrateIfAllowed(effect.millis)

                is PairMatchingEffect.VibrateWrong ->
                    hapticService.vibrateIfAllowed(effect.millis)

                PairMatchingEffect.NavigateToMultipleChoice ->
                    onNavigateToMultipleChoice()

                is PairMatchingEffect.CopyToClipboard -> {
                    clipboardCopier.copy(effect.text)
                    showToastRes(SharedRes.strings.copied_to_clipboard)
                }
            }
        }
    }

    // ── Toolbar action handler ─────────────────────────────────────────────────
    LaunchedEffect(screenKey) {
        toolbarActionBus.setActiveScreenKey(screenKey)
        toolbarActionBus.events.collect { event ->
            if (event.screenKey != screenKey) return@collect
            when (event.action as? PairMatchingToolbarAction ?: return@collect) {
                PairMatchingToolbarAction.NewGame ->
                    store.dispatch(PairMatchingAction.NewGame)

                PairMatchingToolbarAction.ShowSolution ->
                    store.dispatch(PairMatchingAction.ShowSolution)

                PairMatchingToolbarAction.Refresh ->
                    store.dispatch(PairMatchingAction.Refresh)

                PairMatchingToolbarAction.CopyPairs -> {
                    val phase = state.gamePhase
                    if (phase is GamePhase.Playing && phase.solutionIsShown) {
                        store.dispatch(PairMatchingAction.ShowCopyPairsDialog)
                    } else {
                        store.dispatch(PairMatchingAction.RequestCopy(copyAll = true))
                    }
                }

                PairMatchingToolbarAction.CreatePairs ->
                    store.dispatch(PairMatchingAction.SelectTopic(0))

                PairMatchingToolbarAction.Settings ->
                    onOpenSettings()
            }
        }
    }

    PairMatchingGameContent(
        state = state,
        isDragOnLongClick = store.isDragOnLongClickEnabled,
        onAction = store::dispatch,
    )
}

// ─── Pure-UI content (no Koin — easily previewable and testable) ──────────────

@Composable
fun PairMatchingGameContent(
    state: PairMatchingState,
    isDragOnLongClick: Boolean,
    onAction: (PairMatchingAction) -> Unit,
    modifier: Modifier = Modifier,
) {
    SetScreenIsLoading(state.isLoading)

    Box(modifier = modifier.fillMaxSize()) {

        when (val phase = state.gamePhase) {
            is GamePhase.Idle -> Unit

            is GamePhase.Playing -> Column(modifier = Modifier.fillMaxSize()) {
                PairMatchingProgress(
                    matched = phase.correctPairsSoFar,
                    total = phase.numberOfPairs,
                )
                PairMatchingTwoColumnGame(
                    keyItems = phase.keyItems,
                    valueItems = phase.valueItems,
                    keyTitle = phase.topic.keyTitle,
                    valueTitle = phase.topic.valueTitle,
                    isDragOnLongClick = isDragOnLongClick,
                    onAttemptMatch = { keyId, valueId ->
                        onAction(PairMatchingAction.AttemptMatch(keyId = keyId, valueId = valueId))
                    },
                    modifier = Modifier
                        .weight(1f)
                        .fillMaxWidth()
                        .padding(8.dp),
                )
            }

            is GamePhase.Sortable -> Column(modifier = Modifier.fillMaxSize()) {
                SortableList(
                    data = phase.items,
                    onSortCompleted = { onAction(PairMatchingAction.SortableCompleted) },
                )
            }
        }

        PairMatchingDialogs(
            dialogState = state.dialogState,
            onAction = onAction,
            onItemClicked = { index: Int, _ -> onAction(PairMatchingAction.SelectTopic(index)) },
            onConfirmCopyAll = { onAction(PairMatchingAction.RequestCopy(copyAll = true)) },
            onConfirmCopyOnlyMissing = { onAction(PairMatchingAction.RequestCopy(copyAll = false)) },
        )
    }
}

// ─── Previews ─────────────────────────────────────────────────────────────────

@Preview(showBackground = true, name = "GameScreen — Playing")
@Composable
private fun GameScreenPlayingPreview() {
    val topic = PairsTopicItem(
        topic = "מלכי ישראל", source = "(מלכים א)",
        keyTitle = "מלך", valueTitle = "עיר בירה",
        pairList = listOf(PairItem("דוד", "חברון"), PairItem("שלמה", "ירושלים"), PairItem("שאול", "גבעה")),
    )
    val keys = listOf(
        KeyItem(id = 0, rawText = "דוד"),
        KeyItem(id = 1, rawText = "שלמה"),
        KeyItem(id = 2, rawText = "שאול", matchedValueRaw = "גבעה"),
    )
    val values = listOf(
        ValueItem(id = 10, rawText = "חברון"),
        ValueItem(id = 11, rawText = "ירושלים"),
        ValueItem(id = 12, rawText = "גבעה", isVisible = false),
    )
    PreviewTheme {
        PairMatchingGameContent(
            state = PairMatchingState(
                dialogState = PairMatchingState.DialogState.None,
                gamePhase = GamePhase.Playing(topic = topic, keyItems = keys, valueItems = values),
            ),
            isDragOnLongClick = false,
            onAction = {},
        )
    }
}

@Preview(showBackground = true, name = "GameScreen — Loading")
@Composable
private fun GameScreenLoadingPreview() {
    PreviewTheme {
        PairMatchingGameContent(
            state = PairMatchingState(dialogState = PairMatchingState.DialogState.None, isLoading = true),
            isDragOnLongClick = false,
            onAction = {},
        )
    }
}

@Preview(showBackground = true, name = "GameScreen — ToC dialog")
@Composable
private fun GameScreenToCPreview() {
    val items = listOf(
        SpecialTopicItem("צור זוגות בעצמך", "(בפיתוח)"),
        SpecialTopicItem("שאלות אמריקאיות", "(כל הרשימות)"),
    ).toImmutableList()
    PreviewTheme {
        PairMatchingGameContent(
            state = PairMatchingState(dialogState = PairMatchingState.DialogState.TableOfContents(items)),
            isDragOnLongClick = false,
            onAction = {},
        )
    }
}
